import type { CharacterId } from './config'
const CLIPS: Record<CharacterId,string> = {
  cow:'/assets/audio/cow-mama-short.mp3', frog:'/assets/audio/nailong-laugh-short.mp3', mouse:'/assets/audio/kangaroo-feidudu-sample.mp3',
}
export class AudioFeedback {
  context?: AudioContext
  muted = false
  lastHit = 0
  private buffers = new Map<CharacterId,AudioBuffer>()
  private loading = new Map<CharacterId,Promise<AudioBuffer | undefined>>()
  private failed = new Set<CharacterId>()
  private current?: AudioBufferSourceNode
  private currentCharacter?: CharacterId
  private generation = 0
  private lastVoice = -100
  status(id:CharacterId) { return this.buffers.has(id)?'ready':this.failed.has(id)?'failed':this.loading.has(id)?'loading':'idle' }
  unlock() {
    if (!this.context) { try { this.context = new AudioContext() } catch { return } }
    if (this.context.state === 'suspended') void this.context.resume()
    for(const id of ['cow','frog','mouse'] as CharacterId[]) void this.load(id)
  }
  private load(id:CharacterId):Promise<AudioBuffer | undefined> {
    if(this.buffers.has(id)) return Promise.resolve(this.buffers.get(id))
    if(this.loading.has(id)) return this.loading.get(id)!
    if(!this.context) return Promise.resolve(undefined)
    const context=this.context
    const promise=fetch(CLIPS[id]).then(response=>{if(!response.ok)throw new Error('Audio unavailable');return response.arrayBuffer()}).then(bytes=>context.decodeAudioData(bytes)).then(buffer=>{this.buffers.set(id,buffer);return buffer}).catch(()=>{this.failed.add(id);return undefined})
    this.loading.set(id,promise);return promise
  }
  stopVoices() {
    this.generation++
    if(this.current) { try{this.current.stop()}catch{/* Already ended. */} }
    this.current=undefined;this.currentCharacter=undefined
  }
  async playCharacter(id:CharacterId,preview=false) {
    if(this.muted) return false
    this.unlock();const context=this.context;if(!context)return false
    if(!preview && this.current && this.currentCharacter===id)return true
    if(!preview && context.currentTime-this.lastVoice<(id==='frog'?2.5:3.5))return true
    const token=this.generation
    const buffer=await this.load(id)
    if(!buffer || this.muted || token!==this.generation)return false
    if(preview || this.current) this.stopVoices()
    const source=context.createBufferSource();const gain=context.createGain()
    source.buffer=buffer;gain.gain.value=id==='frog'?.55:.68;source.connect(gain);gain.connect(context.destination)
    this.current=source;this.currentCharacter=id;this.lastVoice=context.currentTime
    source.start(0,0,Math.min(buffer.duration,id==='frog'?4.4:5.5))
    source.onended=()=>{source.disconnect();gain.disconnect();if(this.current===source){this.current=undefined;this.currentCharacter=undefined}}
    return true
  }
  play(type:string) {
    if(this.muted || !this.context || this.context.state!=='running')return
    const ctx=this.context;const now=ctx.currentTime
    if(type==='hit' && now-this.lastHit<.065)return
    if(type==='hit')this.lastHit=now
    const settings:Record<string,[number,number,number]>={hit:[150,55,.09],kill:[210,90,.11],hurt:[95,45,.18],collect:[650,950,.07],level:[440,880,.32],skill:[80,260,.35],boss:[120,55,.5],won:[520,1040,.6],lost:[220,55,.6]}
    const spec=settings[type];if(!spec)return
    const oscillator=ctx.createOscillator();const gain=ctx.createGain()
    oscillator.type=type==='hit'||type==='skill'?'triangle':'sine';oscillator.frequency.setValueAtTime(spec[0],now);oscillator.frequency.exponentialRampToValueAtTime(spec[1],now+spec[2])
    const volume=(type==='collect'?.03:.07)*(this.current?.2:1)
    gain.gain.setValueAtTime(.0001,now);gain.gain.exponentialRampToValueAtTime(volume,now+.008);gain.gain.exponentialRampToValueAtTime(.0001,now+spec[2])
    oscillator.connect(gain);gain.connect(ctx.destination);oscillator.start(now);oscillator.stop(now+spec[2]+.02);oscillator.onended=()=>{oscillator.disconnect();gain.disconnect()}
  }
}
